import {
  formatSurfaceTotals,
  formatSurfaceType,
  LABOUR_PRICING_METHOD,
  workTypeLabel,
} from "./estimates";
import {
  computeAreaBreakdown,
  computeLabourSurfaceSqft,
  getLabourClientRate,
  getMaterialClientUnitPrice,
  getMaterialOurCost,
  hasPriceOverride,
} from "./estimateLinePricing";

/** Our cost per unit — INTERNAL only (margin). Customer-facing math uses getMaterialUnitPrice. */
export function getMaterialUnitCost(row, materialsCatalog) {
  return getMaterialOurCost(row, materialsCatalog || []);
}

/** Customer unit price (client price, line override applied) — same as the quote (F01). */
export function getMaterialUnitPrice(row, materialsCatalog) {
  return getMaterialClientUnitPrice(row, materialsCatalog || []);
}

export function computeMaterialLineTotal(row, materialsCatalog) {
  const quantity =
    row.quantity != null && row.quantity !== ""
      ? Number(row.quantity) || 0
      : 0;
  return quantity * getMaterialUnitPrice(row, materialsCatalog);
}

export function computeWorkTypeMaterialsTotal(workType, materialsCatalog) {
  const materials = workType?.materials || [];
  return materials.reduce(
    (sum, row) => sum + computeMaterialLineTotal(row, materialsCatalog),
    0,
  );
}

export function computeWorkTypeTotalSqft(workType) {
  const totals = formatSurfaceTotals(workType?.surface_totals);
  return totals.reduce((sum, row) => sum + row.sqft, 0);
}

function getLabourUnitRate(row) {
  return getLabourClientRate(row);
}

/** Sq ft a per sq ft labour line bills: chosen surfaces' net area, else the whole feature. */
export function computeLabourSqft(row, workType) {
  if (!workType) return 0;
  const picked = computeLabourSurfaceSqft(row, workType);
  if (picked != null) return picked;
  const areas = (workType.sections || []).filter((section) => section?.record_kind !== "deduction");
  if (areas.length) {
    return computeAreaBreakdown(workType).reduce((sum, area) => sum + area.net, 0);
  }
  return computeWorkTypeTotalSqft(workType);
}

function labourPriceNote(row, workType, { showAdjustment = true } = {}) {
  const bits = [];
  if (showAdjustment && hasPriceOverride(row)) bits.push("adjusted");
  const ids = Array.isArray(row?.surface_section_ids) ? row.surface_section_ids.filter(Boolean) : [];
  if (ids.length && workType) {
    const names = computeAreaBreakdown(workType)
      .filter((area) => ids.includes(area.section.section_id))
      .map((area) => area.section.name || formatSurfaceType(area.section.surface_type))
      .filter(Boolean);
    if (names.length) bits.push(names.join(", "));
  }
  return bits.length ? ` · ${bits.join(" · ")}` : "";
}

export function computeLabourLineTotal(row, workType = null) {
  const method = row.pricing_method || LABOUR_PRICING_METHOD.HOURLY;
  const rate = getLabourUnitRate(row);

  if (method === LABOUR_PRICING_METHOD.SQUARE_FOOT) {
    return computeLabourSqft(row, workType) * rate;
  }

  const hours = Number(row.estimated_hours) || 0;
  return hours * rate;
}

export function formatLabourLineDetail(row, workType, { showPricing = true, showAdjustment = true } = {}) {
  const method = row.pricing_method || LABOUR_PRICING_METHOD.HOURLY;
  const hasRate = row.hourly_rate != null && row.hourly_rate !== "";

  const note = labourPriceNote(row, workType, { showAdjustment });
  if (method === LABOUR_PRICING_METHOD.SQUARE_FOOT) {
    const sqft = computeLabourSqft(row, workType);
    const sqftLabel = `${sqft.toFixed(1)} sqft`;
    if (showPricing && hasRate) {
      return `${sqftLabel} × ${formatCurrency(getLabourUnitRate(row))}/sqft${note}`;
    }
    return `${sqftLabel}${note}`;
  }

  const hours = Number(row.estimated_hours);
  if (showPricing && hasRate) {
    return `${hours} hrs × ${formatCurrency(getLabourUnitRate(row))}/hr${note}`;
  }
  return `${hours} hrs${note}`;
}

export function computeWorkTypeLabourTotal(workType) {
  const labour = workType?.labour || [];
  return labour.reduce(
    (sum, row) => sum + computeLabourLineTotal(row, workType),
    0,
  );
}

export function computeWorkTypeTotal(workType, materialsCatalog) {
  return (
    computeWorkTypeMaterialsTotal(workType, materialsCatalog) +
    computeWorkTypeLabourTotal(workType)
  );
}

export function computeLocationTotal(location, materialsCatalog) {
  const workTypes = location?.workTypes || [];
  return workTypes.reduce(
    (sum, wt) => sum + computeWorkTypeTotal(wt, materialsCatalog),
    0,
  );
}

export function computeEstimateTotals(estimateTree, materialsCatalog) {
  const locations = (estimateTree?.locations || []).map((location) => {
    const workTypes = (location.workTypes || []).map((workType) => {
      const materialsTotal = computeWorkTypeMaterialsTotal(
        workType,
        materialsCatalog,
      );
      const labourTotal = computeWorkTypeLabourTotal(workType);
      const workTypeTotal = materialsTotal + labourTotal;
      const label = workTypeLabel(workType);

      return {
        workTypeId: workType.estimate_feature_id,
        label,
        materialsTotal,
        labourTotal,
        workTypeTotal,
      };
    });

    const locationTotal = workTypes.reduce(
      (sum, wt) => sum + wt.workTypeTotal,
      0,
    );

    return {
      locationId: location.room_id,
      locationName: location.name,
      isDefaultScope: location.is_default_scope === true,
      workTypes,
      locationTotal,
    };
  });

  const calculatedMaterialsTotal = locations.reduce(
    (sum, loc) =>
      sum + loc.workTypes.reduce((s, wt) => s + wt.materialsTotal, 0),
    0,
  );
  const calculatedLabourTotal = locations.reduce(
    (sum, loc) =>
      sum + loc.workTypes.reduce((s, wt) => s + wt.labourTotal, 0),
    0,
  );
  const calculatedEstimateTotal =
    calculatedMaterialsTotal + calculatedLabourTotal;

  const wasteAdjustment = Number(estimateTree?.waste_adjustment) || 0;
  const travelAdjustment = Number(estimateTree?.travel_adjustment) || 0;
  const miscAdjustment = Number(estimateTree?.misc_adjustment) || 0;
  const adjustedTotal =
    calculatedEstimateTotal +
    wasteAdjustment +
    travelAdjustment +
    miscAdjustment;

  const storedFinal = estimateTree?.final_estimate_price;
  const finalEstimatePrice =
    storedFinal == null || storedFinal === ""
      ? null
      : Number(storedFinal);

  return {
    locations,
    calculatedMaterialsTotal,
    calculatedLabourTotal,
    calculatedEstimateTotal,
    wasteAdjustment,
    travelAdjustment,
    miscAdjustment,
    adjustedTotal,
    finalEstimatePrice,
  };
}

export function formatCurrency(amount) {
  const value = Number(amount);
  const safe = Number.isFinite(value) ? value : 0;
  return `$${safe.toFixed(2)}`;
}
